import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import LookupPicker from '@/components/LookupPicker';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/kit';
import { FormDialog, PickSelect } from '@/components/inputs';
import { TICKET_CATEGORIES, TICKET_PRIORITIES, TICKET_STATUSES } from '@/shared/constants';
import { committed, fromLocalInput, idOf, logActivity, toLocalInput } from '@/shared/data';

function fromTicket(t) {
  return {
    Name: t?.Name ?? '',
    order_c: idOf(t?.order_c),
    email_c: t?.email_c ?? '',
    status_c: t?.status_c || 'Open',
    priority_c: t?.priority_c || 'Normal',
    category_c: t?.category_c || 'Setup',
    last_reply_at_c: toLocalInput(t?.last_reply_at_c),
  };
}

/** Open a ticket for a customer, or edit any ticket's details. */
export default function TicketAdminDialog({ open, onOpenChange, ticket, onSaved }) {
  const editing = !!ticket;
  const [form, setForm] = useState(fromTicket(ticket));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(fromTicket(ticket));
  }, [open]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  async function pickOrder(id) {
    set('order_c')(id || null);
    if (!id) return;
    const r = await sdk.table('orders_c').select(['Name', 'email_c']).get(id);
    const email = r?.data?.email_c;
    if (email) setForm((f) => ({ ...f, email_c: f.email_c || email }));
  }

  async function submit() {
    if (!form.Name.trim()) {
      toast.error('Give the ticket a subject');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email_c.trim())) {
      toast.error('Add the customer email');
      return;
    }
    const fields = {
      Name: form.Name.trim(),
      order_c: form.order_c || null,
      email_c: form.email_c.trim().toLowerCase(),
      status_c: form.status_c,
      priority_c: form.priority_c,
      category_c: form.category_c,
      last_reply_at_c: fromLocalInput(form.last_reply_at_c),
    };
    setSaving(true);
    try {
      const res = editing
        ? await sdk.table('tickets_c').update({ Id: ticket.Id, ...fields })
        : await sdk.table('tickets_c').create(fields);
      const saved = committed(res, editing ? 'Ticket updated' : 'Ticket opened');
      if (!saved) return;
      if (!editing) logActivity('Ticket', `Opened ${fields.Name}`, fields.Name, fields.email_c);
      onOpenChange(false);
      setForm(fromTicket(null));
      onSaved?.(saved);
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Edit ticket' : 'Open a ticket'}
      description={editing ? undefined : 'For a problem a customer reported by DM. Add the first message in the thread after.'}
      onSubmit={submit}
      saving={saving}
      submitLabel={editing ? 'Save changes' : 'Open ticket'}
      busyLabel={editing ? 'Saving…' : 'Opening…'}
      wide
    >
      <Field label="Subject" htmlFor="t-name">
        <Input
          id="t-name"
          value={form.Name}
          onChange={(e) => set('Name')(e.target.value)}
          placeholder="Buffering on the sports channels"
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Order" htmlFor="t-order" hint="Optional. Fills in the email.">
          <LookupPicker
            table="orders_c"
            searchField="Name"
            value={form.order_c}
            onChange={pickOrder}
            placeholder="Search order codes…"
            clearable
          />
        </Field>
        <Field label="Customer email" htmlFor="t-email">
          <Input
            id="t-email"
            type="email"
            value={form.email_c}
            onChange={(e) => set('email_c')(e.target.value)}
            placeholder="maya@example.com"
          />
        </Field>
        <Field label="Status" htmlFor="t-status">
          <PickSelect id="t-status" value={form.status_c} onChange={set('status_c')} values={TICKET_STATUSES} placeholder="Choose a status" />
        </Field>
        <Field label="Priority" htmlFor="t-priority">
          <PickSelect id="t-priority" value={form.priority_c} onChange={set('priority_c')} values={TICKET_PRIORITIES} placeholder="Choose a priority" />
        </Field>
        <Field label="Category" htmlFor="t-category">
          <PickSelect id="t-category" value={form.category_c} onChange={set('category_c')} values={TICKET_CATEGORIES} placeholder="Choose a category" />
        </Field>
        <Field label="Last reply" htmlFor="t-last" hint="Set automatically when anyone replies.">
          <Input
            id="t-last"
            type="datetime-local"
            value={form.last_reply_at_c}
            onChange={(e) => set('last_reply_at_c')(e.target.value)}
            placeholder="2026-11-02T20:00"
          />
        </Field>
      </div>
    </FormDialog>
  );
}
